import { axios } from '@api/axios.js';
import {
  addCadFile,
  addModelManualToFolder,
  getPresignedUrl,
} from '@api/projects.js';
import { Collapse } from '@components/Common/Collapse.js';
import IconButton from '@components/Common/IconButton.js';
import TaskCreateDialog from '@components/Viewport/TaskCreateDialog.js';
import { IS_APP, IS_MOBILE } from '@constants/global.js';
import { route } from '@router/index.js';
import ProjectsState from '@states/Projects';
import UserState from '@states/User.js';
import { UIDiv, UIText } from '@ui';
import {
  createPaginationControls,
  initProjectPagination,
} from '@utils/PaginationHandler.js';
import Fuse from 'fuse.js';
import ProjectGridItem from './item.js';
import { S3_BUCKET } from '/environment.js';

export default class GridView extends UIDiv {
  constructor(accessControl, selectedProjectType, searchTerm = '') {
    super();
    this.accessControl = accessControl;
    this.selectedProjectType = selectedProjectType;
    this.searchTerm = searchTerm; // Store search term for filtering

    this.canCreate = accessControl.hasPermission('create');
    this.canUpdate = accessControl.hasPermission('update');
    this.canDelete = accessControl.hasPermission('delete');
    this.canShare = accessControl.hasPermission('share');
    this.canMove = accessControl.hasPermission('move');
    this.canDownload = accessControl.hasPermission('download');
    this.gridContainer = null; // Store reference
    this.projectPagination = new Map();
    this.folders = ProjectsState.projects.folders;
    this.tasks = ProjectsState.projects.tasks;
    this.lazyLoadThreshold = 15;
    this.tasksPerPage = 10; // Number of tasks to show per page

    // Subscribe to ProjectsState changes to refresh rows when projects are added/removed/updated
    this.subscribeFromProjectsState = ProjectsState.subscribe(projects => {
      this.handleProjectsStateChange(projects);
    });

    this.setClass('flex flex-wrap justify-center overflow-y-auto max-h-full');
    this.setStyle('gap', ['var(--N2)']);
    this.setStyle('padding', ['var(--N1)']);
    this.render();
  }

  initProjectPagination(projectId) {
    return initProjectPagination(this, projectId);
  }

  render() {
    this.clear();
    let items = this.folders;
    if (this.searchTerm && this.searchTerm.trim()) {
      items = this.getFilteredItems(items);
    }
    if (items.length === 0) {
      const noProjectsMessage = new UIText(
        i18next.t('pages.projects.noProjectsFound'),
      );
      noProjectsMessage.setClass('text-gray-500 text-center w-full');
      this.add(noProjectsMessage);
      return;
    }
    items.forEach(project => {
      const projectContentContainer = new UIDiv();
      projectContentContainer.setClass('flex flex-wrap justify-center');
      projectContentContainer.dataset('projectId', project.id);
      this.renderProjectContent(project, projectContentContainer);

      const projectHeader = new UIDiv();
      projectHeader.setClass(
        'flex justify-between w-[calc(100%-10px)] pr-[var(--N1)]',
      );
      const projectTitle = new UIText(project.name);
      projectTitle.setClass(
        'text-lg font-bold overflow-hidden text-ellipsis pr-10',
      );
      projectHeader.add(projectTitle);

      const addTaskButton = new IconButton({
        iconName: 'plus',
        color: 'none',
        text: IS_MOBILE() ? '' : i18next.t('pages.projects.addTask'),
        iconSize: IS_MOBILE() ? 21 : 16,
        onClick: event => {
          event.stopPropagation();
          this.handleTaskCreation(project);
        },
        buttonClass: '',
      });
      if (this.canCreate) {
        projectHeader.add(addTaskButton);
      }

      const collapse = new Collapse({
        title: projectHeader,
        content: projectContentContainer,
        togglePos: 'back',
        isOpen: true,
      });
      this.add(collapse);
    });
  }

  /**
   * Handle search input and refresh the view
   * @param {string} searchTerm - The search term
   */
  handleSearch(searchTerm) {
    this.searchTerm = searchTerm ? searchTerm.toLowerCase() : '';
    this.render();
  }

  handleProjectsStateChange(projects) {
    this.folders = projects.folders;
    this.tasks = projects.tasks;
    // Render the grid with the current items
    this.render();
  }

  renderProjectContent(project, container) {
    container.clear();
    const pagination = this.initProjectPagination(project.id);

    const tasks = this.tasks.filter(task => {
      return (
        task.parentId === project.id ||
        (project.id === 'unsorted-tasks' &&
          task.parentId === this.rootProjectId)
      );
    });
    const totalTasks = tasks.length;

    if (totalTasks > this.lazyLoadThreshold) {
      const startIndex = (pagination.currentPage - 1) * this.tasksPerPage;
      const endIndex = startIndex + this.tasksPerPage;
      const currentTasks = tasks.slice(startIndex, endIndex);

      currentTasks.forEach(task => {
        container.add(
          new ProjectGridItem({
            model: task,
            onClick: () => {
              const params = new URLSearchParams(window.location.search);
              params.set('sharingId', task.sharingId);
              if (IS_APP()) {
                params.set('isApp', 'true');
              }
              const newSearch = params.toString();
              const newUrl = `/viewer?${newSearch}&from=projects`;
              route(newUrl);
            },
            canCreate: this.canCreate,
            canShare: this.canShare,
            canDelete: this.canDelete,
            canUpdate: this.canUpdate,
            canMove: this.canMove,
            canDownload: this.canDownload,
          }),
        );
      });

      const controls = createPaginationControls(
        this,
        project.id,
        totalTasks,
        0,
      );
      const paginationControls = new UIDiv().setClass(
        'flex items-center justify-center p-3 gap-4 w-full gap-2',
      );
      paginationControls.add(controls);
      container.add(paginationControls);
    } else {
      if (tasks.length > 0) {
        tasks.forEach(task => {
          container.add(
            new ProjectGridItem({
              model: task,
              onClick: () => {
                const params = new URLSearchParams(window.location.search);
                params.set('sharingId', task.sharingId);
                if (IS_APP()) {
                  params.set('isApp', 'true');
                }
                const newSearch = params.toString();
                const newUrl = `/viewer?${newSearch}&from=projects`;
                route(newUrl);
              },
              canCreate: this.canCreate,
              canShare: this.canShare,
              canDelete: this.canDelete,
              canUpdate: this.canUpdate,
              canMove: this.canMove,
              canDownload: this.canDownload,
            }),
          );
        });
      } else {
        container.setValue(i18next.t('pages.projects.noTasksInProject'));
      }
    }
  }

  refreshProjectContent(projectId) {
    const projectContentContainer = this.find(
      `[data-project-id="${projectId}"]`,
    )[0];
    const project = this.folders.find(f => f.id === projectId);
    if (project && projectContentContainer) {
      this.renderProjectContent(project, projectContentContainer);
    }
  }

  async handleTaskCreation(project) {
    const dialog = new TaskCreateDialog({
      title: i18next.t('pages.projects.createNewTask'),
      projectName: project.name,
    });
    if (project.id === 'unsorted-tasks') {
      project.id = this.originalProjects.rootFolderId;
    }

    try {
      const result = await dialog.show();
      if (!result) return;

      const { name: taskName, files } = result;
      if (!taskName && (!files || files.length === 0)) return;
      dialog.setLoading(true);
      await this.createTask({
        taskName,
        files,
        projectId: project.id,
        dialog,
      });
    } catch (error) {
      console.error('Error adding task:', error);
      dialog.handleError(error);
    }
  }

  async createTask({ taskName, files, projectId, dialog }) {
    const cadFilePayload = {
      name: taskName,
      modelFolderId: projectId,
      reconstructions: [],
    };

    const cadFileResponse = await addCadFile(cadFilePayload);
    if (
      cadFileResponse &&
      cadFileResponse.status === 'success' &&
      cadFileResponse.response.cadFileID
    ) {
      const cadFileId = cadFileResponse.response.cadFileID;
      const scanningTypeID = 3;
      if (files && files.length > 0) {
        for (const { file, isPanorama } of files) {
          const fileKey = `${file.name}-${file.size}-${file.lastModified}`;
          const fileType = file.name.split('.').pop();
          if (fileType == null) {
            throw new Error('Missing file extension.');
          }
          // get presigned url to upload file to s3
          const presignedUrlResponse = await getPresignedUrl(
            scanningTypeID,
            fileType,
          );
          if (presignedUrlResponse.status !== 200) {
            throw new Error('Failed to get upload url.');
          }
          const presignedUrl = presignedUrlResponse.body;
          // upload file to s3
          const s3UploadResponse = await axios.put(presignedUrl, file);
          if (s3UploadResponse.status !== 200) {
            throw new Error('Failed to upload file to bucket.');
          }
          const fileUrl = presignedUrl.split('?')[0];

          const fileMetadata = [
            {
              size: file.size,
              originalname: file.name,
              key: fileUrl.split(S3_BUCKET)[1],
              location: fileUrl,
            },
          ];
          const uploadData = [{ cadFileId, isPanorama }];
          const payload = {
            files: JSON.stringify(fileMetadata),
            uploadData: JSON.stringify(uploadData),
          };

          const manualUploadResponse = await addModelManualToFolder(
            scanningTypeID,
            payload,
          );
          if (manualUploadResponse.error) {
            throw new Error(`Failed to upload file: ${file.name}`);
          }
        }
      }

      if (dialog) {
        dialog.setLoading(false);
        dialog.close();
      }

      const newTask = {
        id: cadFileId,
        name: taskName || 'Untitled',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        thumbnail: null,
        parentId: projectId,
        sharingId: cadFileResponse.response.sharingId,
        uid: UserState.user.uid,
        organizationId:
          this.selectedProjectType === 'team' ? this.selectedOrg : null,
        reconstructions: [],
      };

      const updatedTasks = [newTask, ...ProjectsState.projects.tasks];
      await ProjectsState.setProjects({
        ...ProjectsState.projects,
        tasks: updatedTasks,
      });
      return newTask;
    } else {
      throw new Error('CAD file creation failed');
    }
  }

  /**
   * Filter items using Fuse.js search
   * @param {Array} items - Items to filter
   * @returns {Array} - Filtered items
   */
  getFilteredItems(items) {
    if (!this.searchTerm || !this.searchTerm.trim()) {
      return items;
    }

    const fuseOptions = {
      keys: ['name'],
      threshold: 0.3,
      includeScore: true,
      shouldSort: true,
    };

    const fuse = new Fuse(items, fuseOptions);
    const results = fuse.search(this.searchTerm);

    // Filter results with score <= 0.4 and return the items
    return results
      .filter(result => result.score <= 0.4)
      .map(result => result.item);
  }

  /**
   * Update access control permissions and refresh the view
   */
  updateAccessControl(accessControl) {
    this.accessControl = accessControl;
    this.canCreate = accessControl.hasPermission('create');
    this.canUpdate = accessControl.hasPermission('update');
    this.canDelete = accessControl.hasPermission('delete');

    // Force a refresh of the current view
    this.render();
  }

  destroy() {
    if (this.subscribeFromProjectsState) {
      this.subscribeFromProjectsState();
    }
    this.clear();
  }
}
